import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import { fmtAUD } from '@/lib/format';
import { toast, Toaster } from 'sonner';
import { CreditCard } from 'lucide-react';

export default function Checkout() {
    const { items, subtotal, clear } = useCart();
    const { academy, user } = useAuth();
    const nav = useNavigate();
    const [contactName, setContactName] = useState(user?.username || '');
    const [contactEmail, setContactEmail] = useState(academy?.contact_email || '');
    const [quoteRef, setQuoteRef] = useState('');
    const [notes, setNotes] = useState('');
    const [busy, setBusy] = useState(false);

    if (items.length === 0) {
        return (
            <div className="text-center py-20">
                <div className="font-mono-data text-xs uppercase text-zinc-500 mb-3">Checkout</div>
                <h1 className="font-display text-3xl uppercase tracking-tighter mb-4">No items to checkout.</h1>
                <button onClick={() => nav('/')} className="bg-crimson px-5 py-3 font-display uppercase tracking-wider">
                    Browse catalog
                </button>
            </div>
        );
    }

    const submit = async () => {
        if (!contactName.trim()) {
            toast.error('Contact name required');
            return;
        }
        setBusy(true);
        try {
            const order = await api.post('/orders', {
                contact_name: contactName, contact_email: contactEmail,
                quote_ref: quoteRef, notes,
                lines: items.map((i) => ({ product_id: i.product_id, sku: i.sku, qty: i.qty })),
            });
            const r = await api.post('/payments/checkout/session', {
                order_id: order.data.id,
                origin_url: window.location.origin,
            });
            clear();
            window.location.href = r.data.url;
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Could not start checkout');
            setBusy(false);
        }
    };

    return (
        <div data-testid="checkout-page">
            <Toaster theme="dark" />
            <div className="font-mono-data text-xs uppercase text-crimson tracking-widest mb-2">Step 2 of 2</div>
            <h1 className="font-display text-4xl uppercase tracking-tighter mb-8">Checkout</h1>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                <div className="lg:col-span-7 space-y-6">
                    <div className="border-hairline p-6">
                        <h2 className="font-display text-xl uppercase tracking-tighter mb-5">Order Details</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <label className="block">
                                <div className="font-mono-data text-[10px] uppercase text-zinc-500 mb-1">Academy</div>
                                <input value={academy?.name || ''} disabled
                                       className="w-full bg-surface border border-zinc-800 px-3 py-2 text-zinc-400" />
                            </label>
                            <label className="block">
                                <div className="font-mono-data text-[10px] uppercase text-zinc-500 mb-1">Contact name *</div>
                                <input data-testid="checkout-contact-name" value={contactName}
                                       onChange={(e) => setContactName(e.target.value)} required
                                       className="w-full bg-surface border border-zinc-800 px-3 py-2 text-white" />
                            </label>
                            <label className="block">
                                <div className="font-mono-data text-[10px] uppercase text-zinc-500 mb-1">Contact email</div>
                                <input data-testid="checkout-contact-email" type="email" value={contactEmail}
                                       onChange={(e) => setContactEmail(e.target.value)}
                                       className="w-full bg-surface border border-zinc-800 px-3 py-2 text-white" />
                            </label>
                            <label className="block">
                                <div className="font-mono-data text-[10px] uppercase text-zinc-500 mb-1">Quote reference</div>
                                <input data-testid="checkout-quote-ref" value={quoteRef}
                                       onChange={(e) => setQuoteRef(e.target.value)} placeholder="e.g. Q-2026-01"
                                       className="w-full bg-surface border border-zinc-800 px-3 py-2 text-white" />
                            </label>
                            <label className="block md:col-span-2">
                                <div className="font-mono-data text-[10px] uppercase text-zinc-500 mb-1">Notes</div>
                                <textarea data-testid="checkout-notes" rows={3} value={notes}
                                          onChange={(e) => setNotes(e.target.value)}
                                          className="w-full bg-surface border border-zinc-800 px-3 py-2 text-white" />
                            </label>
                        </div>
                    </div>

                    <div className="border-hairline">
                        <div className="p-5 border-b border-zinc-800">
                            <h2 className="font-display text-xl uppercase tracking-tighter">Line Items</h2>
                        </div>
                        {items.map((i) => (
                            <div key={i.key} className="grid grid-cols-12 items-center px-5 py-3 border-b border-zinc-800/60">
                                <div className="col-span-7 font-body text-sm">
                                    {i.product_name}
                                    <div className="font-mono-data text-xs text-zinc-500">{i.variant_label} · {i.sku}</div>
                                </div>
                                <div className="col-span-2 text-center font-mono-data text-sm">× {i.qty}</div>
                                <div className="col-span-3 text-right font-mono-data text-sm">{fmtAUD(i.unit_price * i.qty)}</div>
                            </div>
                        ))}
                    </div>
                </div>

                <aside className="lg:col-span-5 border-hairline p-6 self-start sticky top-24">
                    <div className="flex items-center gap-2 font-mono-data text-xs uppercase text-zinc-500 mb-2">
                        <CreditCard size={14} /> Secure payment via Stripe
                    </div>
                    <div className="font-display text-5xl mb-2">{fmtAUD(subtotal)}</div>
                    <div className="font-mono-data text-[10px] uppercase text-zinc-500 mb-6">
                        AUD · ex GST · {items.reduce((s, i) => s + i.qty, 0)} units
                    </div>

                    <button
                        data-testid="checkout-submit-btn"
                        onClick={submit}
                        disabled={busy}
                        className="w-full bg-brand hover:bg-brand-hover text-black font-display uppercase tracking-wider py-3 transition-colors disabled:opacity-50">
                        {busy ? 'Redirecting to Stripe…' : 'Submit & Pay'}
                    </button>

                    <div className="mt-6 p-4 border border-zinc-800 bg-surface/40 font-mono-data text-[10px] uppercase text-zinc-500 leading-relaxed">
                        You'll be redirected to Stripe to complete payment. A receipt is emailed automatically. AUD · ex GST.
                    </div>
                </aside>
            </div>
        </div>
    );
}
